/**
 * 素材库页面 — MaterialLibrary（二期批3 翻新，对齐概念稿 materials.html）。
 *
 * GET /api/products/{id}              产品/品牌/公司上下文（资产树定位）
 * GET /api/brands/{id}                品牌层内容素材（定位/口号/人群/卖点）
 * GET /api/companies/{id}             公司层内容素材（简介/业务/优势/规模/区域）
 * GET /api/products/{id}/semantic-assets 产品层语义素材（经详情聚合一并返回）
 *
 * 结构：产品子页签 → 只读说明 → 类型筛选工具栏 → 公司层素材 → 品牌层素材 →
 *       产品层素材（五维词库 + 各类型素材卡）→ 入库说明。
 *
 * 资产树口径（PRD §4.6.3）：素材是公司/品牌/产品三层资产树上的内容字段与语义
 * 素材，非资源池——不适用 R3 共享/专属徽标；适用 R1（选择器/深链定位产品）。
 *
 * 概念稿裁剪：图片类素材（产品图/卖点图/场景图/视频封面）及其尺寸/格式元数据
 * 无接口字段，按"接口没有不虚构"原则跳过；素材类型以 semantic_assets.asset_type
 * 实际枚举为准（文章/参数表/FAQ/事实摘要/术语表/五维词库）。
 */
import { useEffect, useMemo, useState } from "react";
import { useParams, Link as RouterLink } from "react-router-dom";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import {
  BusinessOutlined,
  ExpandMoreOutlined,
  ArticleOutlined,
  TableChartOutlined,
  QuestionAnswerOutlined,
  ListAltOutlined,
  CodeOutlined,
  HubOutlined,
  LabelOutlined,
} from "@mui/icons-material";
import PageHeader from "../components/PageHeader";
import Badge from "../components/Badge";
import Loading from "../components/Loading";
import EmptyState from "../components/EmptyState";
import { getProductDetail, getProductSemanticAssets } from "../api/products";
import { getBrand } from "../api/brands";
import { getCompany } from "../api/companies";
import { useCompanyStore } from "../store/companyStore";
import { WORD_LIBRARY_DIMENSIONS } from "../utils/constants";
import type { Brand, Company, SemanticAsset } from "../types";

/** 素材类型配置。 */
const ASSET_TYPE_CONFIG: Record<
  string,
  { label: string; icon: React.ReactNode; color: string }
> = {
  article: { label: "文章", icon: <ArticleOutlined />, color: "primary.main" },
  params_table: { label: "参数表", icon: <TableChartOutlined />, color: "info.main" },
  faq: { label: "FAQ", icon: <QuestionAnswerOutlined />, color: "success.main" },
  fact_sheet: { label: "事实摘要", icon: <ListAltOutlined />, color: "warning.main" },
  glossary: { label: "术语表", icon: <ListAltOutlined />, color: "error.main" },
  word_library: { label: "五维词库", icon: <HubOutlined />, color: "secondary.main" },
};

export default function MaterialLibrary() {
  const { productId } = useParams<{ productId: string }>();
  const selectedProductId = useCompanyStore((s) => s.selectedProductId);
  const effectiveProductId = productId || selectedProductId;

  const [productName, setProductName] = useState("");
  const [brandInfo, setBrandInfo] = useState<Brand | null>(null);
  const [companyInfo, setCompanyInfo] = useState<Company | null>(null);
  const [assets, setAssets] = useState<SemanticAsset[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [typeFilter, setTypeFilter] = useState("");

  /** 产品上下文 + 三层素材（产品详情聚合 + 品牌/公司内容字段）。 */
  useEffect(() => {
    if (!effectiveProductId) {
      setAssets([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError("");
      setBrandInfo(null);
      setCompanyInfo(null);
      try {
        const [detail, assetList] = await Promise.all([
          getProductDetail(effectiveProductId),
          getProductSemanticAssets(effectiveProductId),
        ]);
        if (cancelled) return;
        setProductName(detail.product?.name || "");
        setAssets(assetList);
        // 品牌层/公司层内容素材（补充接口，失败静默不影响主视图）
        const brandId = detail.brand?.id || detail.product?.brand_id;
        const companyId = detail.company?.id || detail.brand?.company_id;
        if (brandId) {
          getBrand(brandId)
            .then((b) => !cancelled && setBrandInfo(b))
            .catch(() => undefined);
        }
        if (companyId) {
          getCompany(companyId)
            .then((c) => !cancelled && setCompanyInfo(c))
            .catch(() => undefined);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "获取素材库失败");
          setAssets([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [effectiveProductId]);

  /** 按类型统计（工具栏徽标）。 */
  const typeCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const a of assets) {
      const t = a.asset_type || "unknown";
      counts[t] = (counts[t] || 0) + 1;
    }
    return counts;
  }, [assets]);

  /** 词库资产（单独提取用于顶部展示）。 */
  const wordLibraryAssets = useMemo(
    () => assets.filter((a) => a.asset_type === "word_library"),
    [assets],
  );

  /** 类型筛选后的产品层素材（词库独立成卡，不在列表重复）。 */
  const filteredAssets = useMemo(() => {
    let list = assets.filter((a) => a.asset_type !== "word_library");
    if (typeFilter) list = list.filter((a) => a.asset_type === typeFilter);
    return list;
  }, [assets, typeFilter]);

  /** 公司层素材字段。 */
  const companyMaterials = useMemo(() => {
    if (!companyInfo) return [];
    return [
      { label: "公司简介", value: companyInfo.company_intro },
      { label: "主营业务", value: companyInfo.main_business },
      { label: "核心优势", value: companyInfo.core_advantage },
      { label: "公司规模", value: companyInfo.company_size },
      { label: "服务区域", value: companyInfo.area_served },
    ].filter((f) => f.value);
  }, [companyInfo]);

  /** 品牌层素材字段。 */
  const brandMaterials = useMemo(() => {
    if (!brandInfo) return [];
    return [
      { label: "品牌定位", value: brandInfo.brand_positioning },
      { label: "品牌口号", value: brandInfo.brand_slogan },
      { label: "目标人群", value: brandInfo.target_audience },
      { label: "核心卖点", value: brandInfo.core_selling_points },
    ].filter((f) => f.value);
  }, [brandInfo]);

  if (!effectiveProductId) {
    return (
      <Box>
        <PageHeader
          title="素材库"
          breadcrumbs={[
            { label: "资产管理" },
            { label: "产品", path: "/products" },
            { label: "素材库" },
          ]}
        />
        <Card>
          <CardContent>
            <EmptyState
              text="请先选择一个产品"
              subtitle="从产品列表进入，或使用页面顶部的产品选择器"
            />
          </CardContent>
        </Card>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box>
        <PageHeader title="素材库" />
        <Loading fullPage />
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title={productName ? `素材库 · ${productName}` : "素材库"}
        breadcrumbs={[
          { label: "资产管理" },
          { label: "产品", path: "/products" },
          ...(productName
            ? [{ label: productName, path: `/products/${effectiveProductId}` }]
            : []),
          { label: "素材库" },
        ]}
        subtitle="公司 → 品牌 → 产品资产树素材聚合 · 投放与内容生成共用素材"
      />

      {/* 产品子页签（对齐概念稿 tabs） */}
      <Tabs
        value={2}
        sx={{ mb: 2, borderBottom: "1px solid", borderColor: "divider" }}
      >
        <Tab
          label="概览"
          component={RouterLink}
          to={`/products/${effectiveProductId}`}
          sx={{ textTransform: "none", fontSize: 13, minHeight: 40 }}
        />
        <Tab
          label="场景分析"
          component={RouterLink}
          to={`/products/${effectiveProductId}/scenes`}
          sx={{ textTransform: "none", fontSize: 13, minHeight: 40 }}
        />
        <Tab
          label="素材库"
          sx={{ textTransform: "none", fontSize: 13, minHeight: 40 }}
        />
        <Tab
          label="事实包"
          component={RouterLink}
          to={`/products/${effectiveProductId}/fact-packs`}
          sx={{ textTransform: "none", fontSize: 13, minHeight: 40 }}
        />
        <Tab
          label="GHS 诊断"
          component={RouterLink}
          to={`/products/${effectiveProductId}/ghs`}
          sx={{ textTransform: "none", fontSize: 13, minHeight: 40 }}
        />
      </Tabs>

      <Alert severity="info" sx={{ mb: 2 }}>
        素材由内容生成 skill 依据资产树产出入库，网站侧只读；类型筛选即同步至
        文案库与投放计划的素材口径。
      </Alert>

      {/* 工具栏：类型筛选（对齐概念稿 badges 工具栏） */}
      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ py: 2, "&:last-child": { pb: 2 } }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <Chip
              label={`全部 ${assets.length}`}
              size="small"
              onClick={() => setTypeFilter("")}
              sx={{
                fontSize: 11,
                height: 22,
                fontWeight: 500,
                cursor: "pointer",
                color: typeFilter === "" ? "primary.main" : "text.secondary",
                bgcolor: typeFilter === "" ? "brand.50" : "semanticBg.neutral",
                border: "1px solid",
                borderColor: typeFilter === "" ? "primary.main" : "transparent",
              }}
            />
            {Object.entries(ASSET_TYPE_CONFIG).map(([key, cfg]) => {
              const count = typeCounts[key] || 0;
              if (count === 0) return null;
              const active = typeFilter === key;
              return (
                <Chip
                  key={key}
                  label={`${cfg.label} ${count}`}
                  size="small"
                  onClick={() => setTypeFilter(active ? "" : key)}
                  sx={{
                    fontSize: 11,
                    height: 22,
                    fontWeight: 500,
                    cursor: "pointer",
                    color: active ? "primary.main" : "text.secondary",
                    bgcolor: active ? "brand.50" : "semanticBg.neutral",
                    border: "1px solid",
                    borderColor: active ? "primary.main" : "transparent",
                  }}
                />
              );
            })}
            <Box sx={{ flexGrow: 1 }} />
            <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
              类型筛选 · 素材按资产树三层组织
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {error ? (
        <Card>
          <CardContent>
            <EmptyState text={error} />
          </CardContent>
        </Card>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {/* 公司层素材 */}
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
                <BusinessOutlined sx={{ color: "primary.main", fontSize: 22 }} />
                <Typography variant="h4" sx={{ flexGrow: 1 }}>
                  公司层素材{companyInfo?.name ? ` · ${companyInfo.name}` : ""}
                </Typography>
                <Badge label="资产树 · 公司" variant="info" />
              </Box>
              {companyMaterials.length > 0 ? (
                <Stack divider={<Divider />} spacing={1.5}>
                  {companyMaterials.map((f) => (
                    <Box key={f.label} sx={{ display: "flex", gap: 2 }}>
                      <Typography
                        sx={{ fontSize: 12, color: "text.secondary", minWidth: 64, flexShrink: 0 }}
                      >
                        {f.label}
                      </Typography>
                      <Typography sx={{ fontSize: 13, lineHeight: 1.8 }}>
                        {f.value}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              ) : (
                <EmptyState
                  text="公司层内容素材未维护"
                  source="在公司档案补全简介/业务/优势后自动展示"
                />
              )}
            </CardContent>
          </Card>

          {/* 品牌层素材 */}
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
                <LabelOutlined sx={{ color: "secondary.main", fontSize: 22 }} />
                <Typography variant="h4" sx={{ flexGrow: 1 }}>
                  品牌层素材{brandInfo?.name ? ` · ${brandInfo.name}` : ""}
                </Typography>
                <Badge label="资产树 · 品牌" variant="info" />
              </Box>
              {brandMaterials.length > 0 ? (
                <Stack divider={<Divider />} spacing={1.5}>
                  {brandMaterials.map((f) => (
                    <Box key={f.label} sx={{ display: "flex", gap: 2 }}>
                      <Typography
                        sx={{ fontSize: 12, color: "text.secondary", minWidth: 64, flexShrink: 0 }}
                      >
                        {f.label}
                      </Typography>
                      <Typography sx={{ fontSize: 13, lineHeight: 1.8 }}>
                        {f.value}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              ) : (
                <EmptyState
                  text="品牌层内容素材未维护"
                  source="在品牌档案补全定位/口号/人群/卖点后自动展示"
                />
              )}
            </CardContent>
          </Card>

          {/* 产品层素材：五维词库 */}
          {wordLibraryAssets.length > 0 && (typeFilter === "" || typeFilter === "word_library") && (
            <Card>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
                  <HubOutlined sx={{ color: "secondary.main", fontSize: 22 }} />
                  <Typography variant="h4" sx={{ flexGrow: 1 }}>
                    五维词库
                  </Typography>
                  <Badge label="资产树 · 产品" variant="info" />
                </Box>
                <Grid container spacing={2}>
                  {WORD_LIBRARY_DIMENSIONS.map((dim) => {
                    const wordData = wordLibraryAssets
                      .map((a) => extractWordLibraryDimension(a, dim.key))
                      .filter(Boolean)
                      .flat();
                    return (
                      <Grid item xs={12} sm={6} md={4} key={dim.key}>
                        <Box
                          sx={{
                            p: 2,
                            borderRadius: 2,
                            bgcolor: `${dim.color}08`,
                            border: `1px solid ${dim.color}20`,
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: 13,
                              fontWeight: 600,
                              color: dim.color,
                              mb: 1,
                            }}
                          >
                            {dim.label}
                          </Typography>
                          {wordData.length > 0 ? (
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                              {wordData.map((word, idx) => (
                                <Chip
                                  key={idx}
                                  label={word}
                                  size="small"
                                  sx={{
                                    fontSize: 11,
                                    bgcolor: `${dim.color}10`,
                                    color: dim.color,
                                  }}
                                />
                              ))}
                            </Box>
                          ) : (
                            <Typography sx={{ fontSize: 11, color: "text.disabled" }}>
                              暂无数据
                            </Typography>
                          )}
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              </CardContent>
            </Card>
          )}

          {/* 产品层素材：各类型素材卡 */}
          {assets.length === 0 ? (
            <Card>
              <CardContent>
                <EmptyState
                  text="暂无产品层语义素材"
                  source="由语义素材 Agent 产出 · 生成后自动展示"
                />
              </CardContent>
            </Card>
          ) : filteredAssets.length === 0 &&
            (typeFilter === "" || typeFilter === "word_library" || wordLibraryAssets.length === 0) ? (
            wordLibraryAssets.length === 0 && (
              <Card>
                <CardContent>
                  <EmptyState
                    text={typeFilter ? "该类型暂无素材" : "暂无其他素材"}
                  />
                </CardContent>
              </Card>
            )
          ) : (
            filteredAssets.map((asset) => (
              <AssetCard key={asset.id} asset={asset} />
            ))
          )}
        </Box>
      )}

      {/* 入库说明（对齐概念稿） */}
      <Card sx={{ mt: 2 }}>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
            <Typography variant="h4">入库说明</Typography>
          </Box>
          <Typography sx={{ fontSize: 12, color: "text.secondary", lineHeight: 1.8 }}>
            素材是公司 → 品牌 → 产品资产树上的内容资产：公司/品牌层为档案内容字段
            （简介/定位/卖点等），产品层为语义素材（文章/参数表/FAQ/事实摘要/术语表/
            五维词库），由内容生成 skill 依据事实包口径产出，入库后经人工复核可用；
            上传与版本管理在 skill 侧完成，网站侧只读。
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
}

// ==================== 素材卡片组件 ====================

/** 单个素材卡片 — 根据 asset_type 渲染不同的人读视图。 */
function AssetCard({ asset }: { asset: SemanticAsset }) {
  const config = ASSET_TYPE_CONFIG[asset.asset_type] || {
    label: asset.asset_type,
    icon: <ArticleOutlined />,
    color: "text.secondary",
  };

  return (
    <Card>
      <CardContent>
        {/* 标题行 */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
          <Box sx={{ color: config.color, display: "flex" }}>{config.icon}</Box>
          <Typography sx={{ fontSize: 15, fontWeight: 600, flexGrow: 1 }}>
            {config.label}
          </Typography>
          <Badge label={asset.asset_type} variant="info" />
        </Box>

        {/* 人读内容 */}
        <Box sx={{ mb: asset.schema_jsonld ? 2 : 0 }}>
          <AssetContent asset={asset} />
        </Box>

        {/* 技术详情（折叠） */}
        {asset.schema_jsonld && (
          <Accordion sx={{ mt: 1, bgcolor: "background.default" }}>
            <AccordionSummary expandIcon={<ExpandMoreOutlined />}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CodeOutlined sx={{ fontSize: 16, color: "text.secondary" }} />
                <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                  技术详情（Schema JSON-LD）
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box
                component="pre"
                sx={{
                  fontSize: 11,
                  bgcolor: "brand.50",
                  p: 1.5,
                  borderRadius: 1,
                  overflow: "auto",
                  maxHeight: 300,
                  whiteSpace: "pre-wrap",
                  fontFamily: "monospace",
                  color: "primary.main",
                  margin: 0,
                }}
              >
                {formatJsonLd(asset.schema_jsonld)}
              </Box>
            </AccordionDetails>
          </Accordion>
        )}
      </CardContent>
    </Card>
  );
}

/** 根据素材类型渲染人读内容。 */
function AssetContent({ asset }: { asset: SemanticAsset }) {
  const content = asset.content;

  if (!content) {
    return <EmptyState text="暂无内容" />;
  }

  switch (asset.asset_type) {
    case "article":
      return <ArticleView content={content} />;
    case "params_table":
      return <ParamsTableView content={content} />;
    case "faq":
      return <FaqView content={content} />;
    case "fact_sheet":
    case "glossary":
      return <KeyValuesView content={content} />;
    case "word_library":
      return <WordLibraryView content={content} />;
    default:
      return (
        <Box
          component="pre"
          sx={{
            fontSize: 12,
            whiteSpace: "pre-wrap",
            fontFamily: "monospace",
            color: "text.primary",
            margin: 0,
          }}
        >
          {typeof content === "string" ? content : JSON.stringify(content, null, 2)}
        </Box>
      );
  }
}

// ==================== 文章视图 ====================

/** 文章视图 — Markdown 渲染。 */
function ArticleView({ content }: { content: any }) {
  const text = typeof content === "string" ? content : String(content);
  const blocks = parseMarkdown(text);

  return (
    <Box>
      {blocks.map((block, idx) => {
        switch (block.type) {
          case "h1":
            return (
              <Typography key={idx} variant="h3" sx={{ mb: 1.5, mt: idx > 0 ? 2 : 0 }}>
                {block.text}
              </Typography>
            );
          case "h2":
            return (
              <Typography key={idx} variant="h4" sx={{ mb: 1, mt: 2 }}>
                {block.text}
              </Typography>
            );
          case "h3":
            return (
              <Typography key={idx} variant="h5" sx={{ mb: 1, mt: 1.5, color: "primary.main" }}>
                {block.text}
              </Typography>
            );
          case "table":
            return <MarkdownTable key={idx} rows={block.rows!} />;
          case "paragraph":
            return (
              <Typography
                key={idx}
                sx={{ fontSize: 13, lineHeight: 1.8, mb: 1, color: "text.primary" }}
              >
                {renderInlineFormatting(block.text || "")}
              </Typography>
            );
          case "list":
            return (
              <Box key={idx} sx={{ mb: 1, pl: 2 }}>
                {block.items!.map((item, i) => (
                  <Typography
                    key={i}
                    sx={{ fontSize: 13, lineHeight: 1.8, color: "text.primary" }}
                  >
                    {"\u2022 "}
                    {renderInlineFormatting(item)}
                  </Typography>
                ))}
              </Box>
            );
          default:
            return null;
        }
      })}
    </Box>
  );
}

/** 解析 Markdown 为块结构。 */
function parseMarkdown(
  text: string,
): Array<{
  type: string;
  text?: string;
  rows?: string[][];
  items?: string[];
}> {
  const lines = text.split("\n");
  const blocks: Array<{ type: string; text?: string; rows?: string[][]; items?: string[] }> = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    // 空行
    if (!line.trim()) {
      i++;
      continue;
    }

    // 标题
    if (line.startsWith("### ")) {
      blocks.push({ type: "h3", text: line.slice(4).trim() });
      i++;
      continue;
    }
    if (line.startsWith("## ")) {
      blocks.push({ type: "h2", text: line.slice(3).trim() });
      i++;
      continue;
    }
    if (line.startsWith("# ")) {
      blocks.push({ type: "h1", text: line.slice(2).trim() });
      i++;
      continue;
    }

    // 表格
    if (line.includes("|") && line.trim().startsWith("|")) {
      const tableLines: string[] = [];
      while (i < lines.length && lines[i].includes("|") && lines[i].trim().startsWith("|")) {
        tableLines.push(lines[i]);
        i++;
      }
      const rows = tableLines
        .filter((l) => !l.trim().match(/^\|[\s-:|]+\|$/))
        .map((l) =>
          l
            .split("|")
            .map((c) => c.trim())
            .filter((c) => c !== ""),
        );
      blocks.push({ type: "table", rows });
      continue;
    }

    // 列表
    if (line.trim().startsWith("- ") || line.trim().startsWith("* ")) {
      const items: string[] = [];
      while (
        i < lines.length &&
        (lines[i].trim().startsWith("- ") || lines[i].trim().startsWith("* "))
      ) {
        items.push(lines[i].trim().slice(2));
        i++;
      }
      blocks.push({ type: "list", items });
      continue;
    }

    // 普通段落
    blocks.push({ type: "paragraph", text: line.trim() });
    i++;
  }

  return blocks;
}

/** 渲染内联格式（**bold** 等）。 */
function renderInlineFormatting(text: string): React.ReactNode {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, idx) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return (
        <Box component="strong" key={idx} sx={{ fontWeight: 600 }}>
          {part.slice(2, -2)}
        </Box>
      );
    }
    return part;
  });
}

/** Markdown 表格渲染。 */
function MarkdownTable({ rows }: { rows: string[][] }) {
  if (!rows || rows.length === 0) return null;
  const [header, ...body] = rows;

  return (
    <TableContainer component={Paper} sx={{ mb: 2, boxShadow: "none" }} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: "background.default" }}>
            {header.map((cell, idx) => (
              <TableCell key={idx} sx={{ fontSize: 12, fontWeight: 600 }}>
                {cell}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {body.map((row, ridx) => (
            <TableRow key={ridx}>
              {row.map((cell, cidx) => (
                <TableCell key={cidx} sx={{ fontSize: 12 }}>
                  {cell}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// ==================== 参数表视图 ====================

/** 参数表视图 — 表格展示。 */
function ParamsTableView({ content }: { content: any }) {
  let rows: Array<{ param: string; value: string }> = [];

  if (typeof content === "string") {
    try {
      const parsed = JSON.parse(content);
      content = parsed;
    } catch {
      return (
        <Typography sx={{ fontSize: 13, whiteSpace: "pre-wrap" }}>{content}</Typography>
      );
    }
  }

  if (Array.isArray(content)) {
    rows = content.map((item: any) => ({
      param: item.param || item.name || item.key || "",
      value: String(item.value || item.val || ""),
    }));
  } else if (typeof content === "object" && content !== null) {
    if (content.rows && Array.isArray(content.rows)) {
      rows = content.rows.map((item: any) => ({
        param: item.param || item.name || item.key || "",
        value: String(item.value || item.val || ""),
      }));
    } else {
      rows = Object.entries(content).map(([key, val]) => ({
        param: key,
        value: String(val),
      }));
    }
  }

  if (rows.length === 0) {
    return <EmptyState text="暂无参数数据" />;
  }

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: "background.default" }}>
            <TableCell sx={{ fontSize: 12, fontWeight: 600, width: "40%" }}>
              参数
            </TableCell>
            <TableCell sx={{ fontSize: 12, fontWeight: 600 }}>
              参数值
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, idx) => (
            <TableRow key={idx}>
              <TableCell sx={{ fontSize: 12, color: "text.secondary" }}>{row.param}</TableCell>
              <TableCell sx={{ fontSize: 12 }}>{row.value}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// ==================== FAQ 视图 ====================

/** FAQ 视图 — 问答对展示。 */
function FaqView({ content }: { content: any }) {
  let faqs: Array<{ question: string; answer: string }> = [];

  if (typeof content === "string") {
    try {
      const parsed = JSON.parse(content);
      content = parsed;
    } catch {
      return (
        <Typography sx={{ fontSize: 13, whiteSpace: "pre-wrap" }}>{content}</Typography>
      );
    }
  }

  if (Array.isArray(content)) {
    faqs = content.map((item: any) => ({
      question: item.question || item.q || "",
      answer: String(item.answer || item.a || ""),
    }));
  } else if (typeof content === "object" && content !== null) {
    if (content.faqs && Array.isArray(content.faqs)) {
      faqs = content.faqs.map((item: any) => ({
        question: item.question || item.q || "",
        answer: String(item.answer || item.a || ""),
      }));
    }
  }

  if (faqs.length === 0) {
    return <EmptyState text="暂无FAQ数据" />;
  }

  return (
    <Stack divider={<Divider />} spacing={2}>
      {faqs.map((faq, idx) => (
        <Box key={idx}>
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: "primary.main", mb: 0.5 }}>
            Q: {faq.question}
          </Typography>
          <Typography sx={{ fontSize: 13, color: "text.primary", lineHeight: 1.6 }}>
            A: {faq.answer}
          </Typography>
        </Box>
      ))}
    </Stack>
  );
}

// ==================== 键值对视图 ====================

/** 键值对视图 — 事实摘要/术语表。 */
function KeyValuesView({ content }: { content: any }) {
  let entries: Array<{ key: string; value: string }> = [];

  if (typeof content === "string") {
    try {
      const parsed = JSON.parse(content);
      content = parsed;
    } catch {
      return (
        <Typography sx={{ fontSize: 13, whiteSpace: "pre-wrap" }}>{content}</Typography>
      );
    }
  }

  if (typeof content === "object" && content !== null) {
    if (Array.isArray(content)) {
      entries = content.map((item: any) => ({
        key: item.key || item.term || item.name || "",
        value: String(item.value || item.definition || item.desc || ""),
      }));
    } else {
      entries = Object.entries(content).map(([key, val]) => ({
        key,
        value: String(val),
      }));
    }
  }

  if (entries.length === 0) {
    return <EmptyState text="暂无数据" />;
  }

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableBody>
          {entries.map((entry, idx) => (
            <TableRow key={idx}>
              <TableCell
                sx={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: "text.secondary",
                  width: "30%",
                  bgcolor: "background.default",
                }}
              >
                {entry.key}
              </TableCell>
              <TableCell sx={{ fontSize: 12 }}>{entry.value}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// ==================== 五维词库视图 ====================

/** 五维词库视图 — 独立展示（用于卡片内嵌时）。 */
function WordLibraryView({ content }: { content: any }) {
  if (typeof content === "string") {
    try {
      content = JSON.parse(content);
    } catch {
      return <EmptyState text="词库数据格式无法解析" />;
    }
  }

  if (!content || typeof content !== "object") {
    return <EmptyState text="暂无词库数据" />;
  }

  return (
    <Grid container spacing={2}>
      {WORD_LIBRARY_DIMENSIONS.map((dim) => {
        const dimData = content[dim.key];
        const coreWords: string[] = dimData?.core || dimData?.keywords || [];
        const extWords: string[] = dimData?.extended || dimData?.extensions || [];
        const allWords = [...coreWords, ...extWords];

        return (
          <Grid item xs={12} sm={6} md={4} key={dim.key}>
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: `${dim.color}08`,
                border: `1px solid ${dim.color}20`,
              }}
            >
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: dim.color, mb: 1 }}>
                {dim.label}
              </Typography>
              {allWords.length > 0 ? (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {allWords.map((word, idx) => (
                    <Chip
                      key={idx}
                      label={word}
                      size="small"
                      sx={{
                        fontSize: 11,
                        bgcolor: idx < coreWords.length ? `${dim.color}15` : `${dim.color}08`,
                        color: dim.color,
                        fontWeight: idx < coreWords.length ? 600 : 400,
                      }}
                    />
                  ))}
                </Box>
              ) : (
                <Typography sx={{ fontSize: 11, color: "text.disabled" }}>
                  暂无数据
                </Typography>
              )}
            </Box>
          </Grid>
        );
      })}
    </Grid>
  );
}

// ==================== 辅助函数 ====================

/** 从词库资产中提取指定维度的词汇。 */
function extractWordLibraryDimension(
  asset: SemanticAsset,
  dimKey: string,
): string[] {
  let content = asset.content;
  if (typeof content === "string") {
    try {
      content = JSON.parse(content);
    } catch {
      return [];
    }
  }
  if (!content || typeof content !== "object") return [];

  const dimData = content[dimKey];
  if (!dimData) return [];

  const core: string[] = dimData.core || dimData.keywords || [];
  const ext: string[] = dimData.extended || dimData.extensions || [];
  return [...core, ...ext];
}

/** 格式化 JSON-LD 字符串（尝试美化输出）。 */
function formatJsonLd(jsonld: string): string {
  try {
    return JSON.stringify(JSON.parse(jsonld), null, 2);
  } catch {
    return jsonld;
  }
}
